import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { FAQ_ITEMS } from '../constants'
import Container from './Container'
import AnimatedSection from './AnimatedSection'
import { accordionContentVariants, iconRotateVariants } from '../utils/animations'

/**
 * FAQSection - Accordion-style FAQ section with 10 questions
 */

const FAQSection: React.FC = () => {
  const [openItems, setOpenItems] = useState<string[]>([])

  const toggleItem = (id: string) => {
    setOpenItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
  }

  const isOpen = (id: string) => openItems.includes(id)

  return (
    <section id="faq" className="bg-background py-24 md:py-32">
      <Container size="lg">
        {/* Section Header */}
        <AnimatedSection className="mx-auto mb-16 max-w-3xl text-center">
          {/* Section Label */}
          <span className="section-label mb-4 block">Información</span>
          <h2 className="accent-line-center mb-6 text-3xl font-bold text-primary md:text-4xl lg:text-5xl">
            Preguntas frecuentes
          </h2>
        </AnimatedSection>

        {/* FAQ Accordion */}
        <AnimatedSection>
          <div className="space-y-4">
            {FAQ_ITEMS.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`rounded-xl border bg-surface transition-all duration-300 ${
                  isOpen(item.id)
                    ? 'border-accent shadow-md'
                    : 'border-border-light hover:border-accent/50'
                }`}
              >
                {/* Question Button */}
                <button
                  onClick={() => toggleItem(item.id)}
                  className="flex w-full items-center justify-between rounded-xl p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                  aria-expanded={isOpen(item.id)}
                  aria-controls={`faq-answer-${item.id}`}
                >
                  <span className="pr-4 text-lg font-semibold text-primary">{item.question}</span>
                  <motion.span
                    variants={iconRotateVariants}
                    animate={isOpen(item.id) ? 'expanded' : 'collapsed'}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown
                      className={`h-6 w-6 ${isOpen(item.id) ? 'text-accent' : 'text-text-muted'}`}
                      aria-hidden={true}
                    />
                  </motion.span>
                </button>

                {/* Answer */}
                <AnimatePresence initial={false}>
                  {isOpen(item.id) && (
                    <motion.div
                      id={`faq-answer-${item.id}`}
                      variants={accordionContentVariants}
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <p className="leading-relaxed text-text-secondary">{item.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </Container>
    </section>
  )
}

export default FAQSection
